import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';

const NotificationContext = createContext({ showNotification: () => {} });

export const useNotification = () => useContext(NotificationContext).showNotification;

export const NotificationProvider = ({ children }) => {
  const [state, setState] = useState({ 
    open: false, 
    title: '', 
    message: '', 
    type: 'info', // info, warning, error, success
    resolve: null,
    showCancel: true 
  });

  const showNotification = useCallback((title, message, type = 'info', showCancel = true) => {
    return new Promise(resolve => setState({ 
      open: true, 
      title, 
      message, 
      type, 
      showCancel, 
      resolve 
    }));
  }, []);

  const onCancel = useCallback(() => {
    if (state.resolve) state.resolve(false);
    setState({ open: false, title: '', message: '', type: 'info', resolve: null, showCancel: true });
  }, [state]);

  const onConfirm = useCallback(() => {
    if (state.resolve) state.resolve(true);
    setState({ open: false, title: '', message: '', type: 'info', resolve: null, showCancel: true });
  }, [state]);

  // ESC tuşu ile kapatma
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27 && state.open) {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [state.open, onCancel]);

  const value = useMemo(() => ({ showNotification }), [showNotification]);

  // Tip bazında icon belirleme - transfer-project renklerine uygun
  const getIcon = () => {
    switch (state.type) {
      case 'warning':
        return (
          <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-bht-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'success':
        return (
          <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {state.open && (
        <div 
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onCancel();
            }
          }}
        >
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-200/50 w-full max-w-md overflow-hidden animate-slide-up">
            {/* Header */}
            <div className="px-6 pt-5 pb-3 border-b border-gray-100/50">
              <div className="flex items-center gap-3">
                {/* Icon */}
                <div className="flex-shrink-0 p-2 rounded-full bg-gray-50/80">
                  {getIcon()}
                </div>
                
                {/* Title */}
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-800 leading-tight">
                    {state.title}
                  </h3>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="text-gray-600 text-sm leading-relaxed space-y-2">
                {state.message.split('\n').map((line, index) => (
                  <div key={index}>
                    {line.startsWith('•') ? (
                      <div className="flex items-start gap-2 ml-2">
                        <span className="text-bht-gray mt-1 text-xs">•</span>
                        <span>{line.substring(1).trim()}</span>
                      </div>
                    ) : (
                      <div>{line}</div>
                    )}
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6">
                {state.showCancel && (
                  <button
                    onClick={onCancel}
                    className="px-5 py-2 text-sm font-medium text-bht-gray bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300/50"
                  >
                    İptal
                  </button>
                )}
                <button
                  onClick={onConfirm}
                  className={`px-5 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                    state.type === 'error' 
                      ? 'bg-gradient-to-r from-bht-red to-red-600 hover:from-red-600 hover:to-red-700 focus:ring-red-400/50'
                      : state.type === 'warning'
                      ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 focus:ring-amber-400/50'
                      : state.type === 'success'
                      ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 focus:ring-emerald-400/50'
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:ring-blue-400/50'
                  }`}
                >
                  Tamam
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
};

// Kullanım kolaylığı için hazır hook
export const useNotificationHelpers = () => {
  const showNotification = useNotification();
  
  return {
    showInfo: (title, message, showCancel = true) => 
      showNotification(title, message, 'info', showCancel),
    showWarning: (title, message, showCancel = true) => 
      showNotification(title, message, 'warning', showCancel),
    showError: (title, message, showCancel = true) => 
      showNotification(title, message, 'error', showCancel),
    showSuccess: (title, message, showCancel = true) => 
      showNotification(title, message, 'success', showCancel),
  };
};
